import React, { useCallback, useRef, useState } from 'react';
import {
  Row, Col, Button, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip,
} from 'reactstrap';
import classnames from 'classnames';
import _ from 'lodash';

import { Intl, LocalTable } from 'components';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import bg from 'assets/img/step3Bg.png';
import { connect } from 'react-redux';
import { handlePromise } from 'utils';
import CampaignService from 'api/Campaign';
import SettingsActions from 'store/reducers/Settings';
import AddContactModal from '../utils/AddContactModal';
import UploadExcelContactModal from '../utils/UploadExcelContactModal';
import ContactFavouriteModal from '../utils/ContactFavouriteModal';
import ContactCrmModal from '../utils/ContactCrmModal';
import GenericContactModal from '../utils/GenericContactModal';
import SearchContactModal from '../utils/SearchContactModal';
import SaveFavouriteModal from '../utils/SaveFavouriteModal';

const Icon = ( { icon, disabled, onClick, id } ) => (
  <Col xs="auto">
    <i
      onClick={disabled ? () => {} : onClick}
      className={classnames( 'fa text-lg cursor-pointer', icon, {
        'text-mp-gray-5': disabled,
        'text-gray-7': !disabled,
      } )}
      id={`saveFavouriteBtn-${id}`}
    />
    <UncontrolledTooltip
        delay={0}
        target={`saveFavouriteBtn-${id}`}
        offset="-30,8"
        className="campaign-wizard-tooltip"
      >
        <Intl id={id} />
      </UncontrolledTooltip>
  </Col>
);

const Step4 = ( {
  campaignId, values, form, onResetContacts, onCompleteStep, toggleLoading, options, dataTeampleader,
} ) => {
  const contacts = values.contacts || [];
  const [addContactModalOpened, setAddContactModalOpened] = useState( false );
  const [uploadExcelModalOpened, setUploadExcelModalOpened] = useState( false );
  const [favouriteModalOpened, setFavouriteModalOpened] = useState( false );
  const [crmModalOpened, setCrmModalOpened] = useState( false );
  const [duplicatedPhonesModalOpened, setDuplicatedPhonesModalOpened] = useState( false );
  const [saveFavouriteModalOpened, setSaveFavouriteModalOpened] = useState( false );
  const [searchModalOpened, setSearchModalOpened] = useState( false );
  const duplicatedContactsRef = useRef( [] );
  const [dropdownOpen, setDropdownOpen] = useState( false );

  const toggleDropdown = useCallback( () => {
    setDropdownOpen( ( state ) => !state );
  }, [] );

  const toggleAddContactModal = useCallback( () => {
    setAddContactModalOpened( !addContactModalOpened );
  }, [addContactModalOpened] );

  const toggleUploadExcelModal = useCallback( () => {
    setUploadExcelModalOpened( !uploadExcelModalOpened );
  }, [uploadExcelModalOpened] );

  const toggleFavouriteModal = useCallback( () => {
    setFavouriteModalOpened( !favouriteModalOpened );
  }, [favouriteModalOpened] );

  const toggleCrmModal = useCallback( () => {
    setCrmModalOpened( ( state ) => !state );
  }, [] );

  const toggleDuplicatedPhonesModal = useCallback( () => {
    setDuplicatedPhonesModalOpened( !duplicatedPhonesModalOpened );
  }, [duplicatedPhonesModalOpened] );

  const toggleSearchModal = useCallback( () => {
    setSearchModalOpened( !searchModalOpened );
  }, [searchModalOpened] );

  const toggleSaveFavouriteModal = useCallback( () => {
    setSaveFavouriteModalOpened( !saveFavouriteModalOpened );
  }, [saveFavouriteModalOpened] );

  const addContacts = useCallback( ( newContacts, type ) => {
    if ( addContactModalOpened ) toggleAddContactModal();
    if ( uploadExcelModalOpened ) toggleUploadExcelModal();
    if ( favouriteModalOpened ) toggleFavouriteModal();
    if ( crmModalOpened ) toggleCrmModal();

    if ( !newContacts.length ) {
      const formattedContacts = _.filter( contacts, ( ( contact ) => contact.type !== type ) );
      form.change( 'contacts', formattedContacts );
      onCompleteStep( [{ step: 3, completed: false }] );
    } else {
      const notAddedContacts = _.filter( newContacts,
        ( item ) => _.findIndex( contacts, { id: item.id } ) === -1 );

      const filteredContacts = [];
      _.map( contacts, ( item ) => {
        if ( item.type !== type ) {
          filteredContacts.push( item );
        } else {
          const index = _.findIndex( newContacts, { id: item.id } );
          if ( index !== -1 ) {
            filteredContacts.push( newContacts[index] );
          }
        }
      } );

      const formattedContacts = [...notAddedContacts, ...filteredContacts];
      const uniqContacts = _.uniqBy( formattedContacts, 'phoneNumber' );
      const duplicatedContacts = _.filter( formattedContacts,
        ( item ) => _.findIndex( uniqContacts, { id: item.id } ) === -1 );
      if ( duplicatedContacts.length ) {
        duplicatedContactsRef.current = duplicatedContacts;
        toggleDuplicatedPhonesModal();
      }
      if ( uniqContacts.length ) {
        form.change( 'contacts', uniqContacts );
        onCompleteStep( [{
          step: 3,
          completed: _.filter( uniqContacts, { selected: true } ).length > 0,
        }] );
      }
    }
  }, [form, contacts, addContactModalOpened, uploadExcelModalOpened, favouriteModalOpened,
    toggleAddContactModal, toggleUploadExcelModal, toggleDuplicatedPhonesModal,
    toggleFavouriteModal, crmModalOpened, toggleCrmModal, onCompleteStep] );

  const toggleSelected = useCallback( ( row ) => {
    if ( row.original.id ) {
      const newContacts = _.cloneDeep( contacts );
      const contactIndex = _.findIndex( newContacts, { id: row.original.id } );
      if ( contactIndex !== -1 ) {
        newContacts[contactIndex].selected = !newContacts[contactIndex].selected;
      }

      const selectedContacts = _.filter( newContacts, { selected: true } ).length;
      onCompleteStep( [{
        step: 3,
        completed: selectedContacts > 0,
      }] );
      form.change( 'contacts', newContacts );
    }
  }, [form, contacts, onCompleteStep] );

  const saveFavourite = useCallback( async ( dataToSend ) => {
    toggleLoading( true );
    const [errors, response] = await handlePromise(
      CampaignService.saveFavouritePhones( campaignId, {
        countryCode: values.phoneNumberCountryCode,
        name: dataToSend.originalName,
        phoneNumbers: _.map( _.filter( contacts, { selected: true } ), ( contact ) => ( {
          nameId: contact.nameId,
          phoneNumber: contact.phoneNumber,
        } ) ),
      } ),
    );
    toggleLoading( false );
    if ( !response.ok ) return errors;
    toggleSaveFavouriteModal();
  }, [campaignId, contacts, values, toggleSaveFavouriteModal, toggleLoading] );

  const selectedContacts = _.filter( contacts, { selected: true } );

  return (
    <>
      <Row className="campaign-wizard-step-4">
        <Col md={6} className="d-flex flex-column justify-content-center">
          <img
            src={bg}
            alt="..."
            className="campaign-wizard-triangle img-fluid d-none d-lg-block"
            style={{ minHeight: 240 }}
          />

          <Row noGutters className="w-100">
            <Col md={9} xs={12} className="m-auto">
              <Button
                color="app-default"
                size="app-sm"
                block
                onClick={toggleAddContactModal}
              >
                <Intl id="addPoneNumberByHand" />
              </Button>
              <Button
                color="app-default"
                size="app-sm"
                block
                className="ml-0 mt-4"
                onClick={toggleUploadExcelModal}
              >
                <Intl id="uploadTwoColumnExcel" />
              </Button>
              <ButtonDropdown className="btn-block btn-app-block mt-0" isOpen={dropdownOpen} toggle={toggleDropdown}>
                <DropdownToggle
                  caret
                  color="app-default"
                  size="app-sm"
                  className="ml-0 mt-4 btn-block"
                >
                  <Intl id="phonesList" />
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={toggleFavouriteModal}>
                    <Intl id="selectListsFavourites" />
                  </DropdownItem>
                  {dataTeampleader && dataTeampleader.isActivate && _.get( options, 'crm.teamleader' )
                  && (
                  <DropdownItem onClick={toggleCrmModal}>
                    <Intl id="selectFromTeamleader" />
                  </DropdownItem>
                  )}
                  {}
                </DropdownMenu>
              </ButtonDropdown>
            </Col>
          </Row>
        </Col>

        <Col md={5} className="mt-3 mt-md-0 p-md-0 pl-lg-3">
          <Row>
            <Col lg="auto">
              <div className="campaign-wizard-table">
                <LocalTable
                  data={contacts.length < 5
                    ? contacts.concat( new Array( 5 - contacts.length ).fill( { empty: true } ) )
                    : contacts}
                  manualPagination
                  onlyTable
                  getRowProps={( row ) => ( {
                    onClick: () => toggleSelected( row ),
                    className: row.original.selected ? 'bg-orange-6' : '',
                  } )}
                  columns={[
                    {
                      Header: 'nameOrId',
                      accessor: 'nameId',
                      customWidth: 110,
                    },
                    {
                      Header: <Intl
                        id="phonesCount"
                        values={{
                          selected: selectedContacts.length,
                          total: contacts.length,
                        }}
                      />,
                      disableTranslate: true,
                      customWidth: 185,
                      accessor: 'phoneNumber',
                      Cell: ( { cell } ) =>{
                        return( cell.value ? parsePhoneNumberFromString(cell.value).formatInternational():"" )},
                    },
                  ]}
                />
              </div>
              <Row className="justify-content-end mt-2">
                <Icon icon="fa-trash-alt" onClick={onResetContacts} id={"clearPhoneList"} disabled={!contacts.length} />
                <Icon icon="fa-search" onClick={toggleSearchModal} id={"findPhoneInList"} disabled={!contacts.length} />
                <Icon icon="fa-save" onClick={toggleSaveFavouriteModal} id={"saveListToFavorites"} disabled={!selectedContacts.length} />
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>

      {addContactModalOpened
      && (
        <AddContactModal
          campaignId={campaignId}
          values={values}
          open={addContactModalOpened}
          onClose={toggleAddContactModal}
          onAddContacts={addContacts}
        />
      )}

      {uploadExcelModalOpened
      && (
        <UploadExcelContactModal
          campaignId={campaignId}
          values={values}
          open={uploadExcelModalOpened}
          onClose={toggleUploadExcelModal}
          onSubmit={toggleUploadExcelModal}
          onAddContacts={addContacts}
        />
      )}

      {favouriteModalOpened
      && (
        <ContactFavouriteModal
          campaignId={campaignId}
          values={values}
          open={favouriteModalOpened}
          onClose={toggleFavouriteModal}
          onAddContacts={addContacts}
        />
      )}

      {crmModalOpened
      && (
        <ContactCrmModal
          campaignId={campaignId}
          values={values}
          open={crmModalOpened}
          onClose={toggleCrmModal}
          onAddContacts={addContacts}
        />
      )}

      {duplicatedPhonesModalOpened
      && (
        <GenericContactModal
          contacts={duplicatedContactsRef.current}
          modalTitle="duplicatedPhones"
          title="duplicatedPhonesSubtitle"
          tableTitle="phonesDuplicatedCount"
          open={duplicatedPhonesModalOpened}
          onClose={toggleDuplicatedPhonesModal}
        />
      )}

      {searchModalOpened
      && (
        <SearchContactModal
          values={values}
          open={searchModalOpened}
          onClose={toggleSearchModal}
          onConfirmContacts={( newContacts ) => {
            toggleSearchModal();
            form.change( 'contacts', newContacts );
          }}
        />
      )}

      {saveFavouriteModalOpened
      && (
        <SaveFavouriteModal
          open={saveFavouriteModalOpened}
          onClose={toggleSaveFavouriteModal}
          onSubmit={saveFavourite}
        />
      )}
    </>
  );
};

const mapDispatchToProps = ( {
  toggleLoading: SettingsActions.toggleLoading,
} );

export default connect( null, mapDispatchToProps )( Step4 );
